/*
 * Mode d'affichage d'une image dans sa box (`box.imageFit`) :
 * - auto (ou absent) : la box prend les proportions de l'image (historique) ;
 * - contain : la box garde sa taille, l'image y tient entière (bandes vides) ;
 * - cover : la box garde sa taille, l'image la remplit, centrée et recadrée
 *   (`imageOffsetX` / `imageOffsetY` décalent l'image dans le cadre).
 */
export const IMAGE_FIT = {
  auto: 'auto',
  contain: 'contain',
  cover: 'cover',
};

/** Vrai quand la box garde sa taille et que l'image s'y ajuste. */
export const isFixedImageFit = (box) =>
  box?.imageFit === IMAGE_FIT.contain || box?.imageFit === IMAGE_FIT.cover;

/**
 * Hauteur à figer au passage en contain/cover : celle que le mode auto
 * affichait (largeur / ratio de l'image), pour que la box ne saute pas.
 * `fallbackRatio` sert tant que le ratio de l'image n'est pas connu.
 */
export const imageBoxFixedHeight = (box, fallbackRatio) => {
  const ratio = box?.content?.ratio || fallbackRatio;
  if (typeof box?.width !== 'number' || !(ratio > 0)) return 'auto';
  return Math.round(box.width / ratio);
};

/**
 * Props du bloc image web : en contain/cover, le cadre prend les proportions
 * de la box et l'image s'y ajuste. Mode auto -> {} (rendu web inchangé).
 */
export const imageFitWebProps = (box) => {
  if (!isFixedImageFit(box)) return {};
  const { width, height } = box;
  if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) {
    return {};
  }
  return {
    mediaFit: box.imageFit,
    mediaAspectRatio: width / height,
    width,
    ...(hasImageOffset(box) ? { mediaPosition: imageObjectPosition(box) } : {}),
  };
};

export const IMAGE_OFFSET_STEP = 10;

const clampOffset = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(-100, Math.min(100, n)) : 0;
};

/**
 * Décalage de l'image dans sa box en mode remplir, de -100 à 100 par axe.
 * C'est un mouvement de l'image, relatif au surplus rogné : 0 = centrée,
 * +100 = image poussée à fond vers la droite / le bas (son bord gauche / haut
 * touche le cadre). Reste juste quand la box est redimensionnée.
 */
export const imageOffset = (box) => ({
  x: clampOffset(box?.imageOffsetX),
  y: clampOffset(box?.imageOffsetY),
});

/** Vrai quand un décalage est appliqué (box en remplir seulement). */
export const hasImageOffset = (box) => {
  if (box?.imageFit !== IMAGE_FIT.cover) return false;
  const { x, y } = imageOffset(box);
  return x !== 0 || y !== 0;
};

/**
 * Translation en px de l'image, dans le rendu où elle est dimensionnée à la
 * main (cf. Image.jsx). Le surplus est la part de l'image qui dépasse le
 * cadre ; la moitié de chaque côté quand elle est centrée.
 */
export const imageCoverTranslate = (box, imageRatio) => {
  const { width, height } = box || {};
  if (typeof width !== 'number' || typeof height !== 'number' || !(imageRatio > 0)) {
    return { x: 0, y: 0 };
  }
  const shownWidth = imageRatio > width / height ? height * imageRatio : width;
  const shownHeight = imageRatio > width / height ? height : width / imageRatio;
  const { x, y } = imageOffset(box);
  return {
    x: (x / 100) * ((shownWidth - width) / 2),
    y: (y / 100) * ((shownHeight - height) / 2),
  };
};

/**
 * `object-position` équivalent, pour le web : l'image poussée vers la droite
 * montre son côté gauche, d'où le signe inversé. Centré -> '50% 50%'.
 */
export const imageObjectPosition = (box) => {
  const { x, y } = imageOffset(box);
  return `${50 - x / 2}% ${50 - y / 2}%`;
};
